import { Link } from 'react-router-dom';
import SEOMeta from '@/components/base/SEOMeta';
import ProductCard from '@/components/base/ProductCard';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { getHotProducts, getNewProducts } from '@/mocks/products';
import { getLatestArticles } from '@/mocks/articles';
import { siteInfo, testimonials } from '@/mocks/site';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: siteInfo.name,
  url: siteInfo.url,
  description: siteInfo.description,
  email: siteInfo.email,
  telephone: siteInfo.phone,
  address: {
    '@type': 'PostalAddress',
    streetAddress: siteInfo.address,
    addressCountry: 'CN',
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: siteInfo.name,
  url: siteInfo.url,
  description: siteInfo.description,
  potentialAction: {
    '@type': 'SearchAction',
    'target': `${siteInfo.url}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const webpageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: `${siteInfo.name} - 首页`,
  description: siteInfo.description,
  url: siteInfo.url,
  isPartOf: {
    '@type': 'WebSite',
    name: siteInfo.name,
    url: siteInfo.url,
  },
};

export default function HomePage() {
  const hotProducts = getHotProducts(8);
  const newProducts = getNewProducts(4);
  const latestArticles = getLatestArticles(4);

  return (
    <>
      <SEOMeta
        title={`${siteInfo.name}·${siteInfo.tagline} | 日本原装正品购物平台`}
        description={siteInfo.description}
        keywords={siteInfo.keywords}
        canonicalUrl={siteInfo.url}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20Japanese%20aesthetic%20composition%20with%20cherry%20blossom%20branches%20and%20soft%20warm%20cream%20backdrop%2C%20minimal%20zen%20atmosphere%2C%20abstract%20artistic%20style%2C%20gentle%20gradient%20from%20cream%20to%20soft%20rose%2C%20no%20text%2C%20clean%20sophisticated%20design&width=1800&height=900&seq=hero-bg-01&orientation=landscape"
          alt="日本精品一区"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <div className="text-center w-full max-w-3xl">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {siteInfo.name}·{siteInfo.tagline}
            </h1>
            <p className="text-base md:text-lg text-white/85 mb-8 max-w-2xl mx-auto leading-relaxed">
              {siteInfo.description}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/beauty"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-md text-sm transition-colors whitespace-nowrap"
              >
                立即选购
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-background-50/15 hover:bg-background-50/25 text-white border border-white/30 rounded-md text-sm font-medium transition-colors whitespace-nowrap"
              >
                了解我们
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Category Section */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              精选品类
            </h2>
            <p className="text-sm text-foreground-500 mt-3 max-w-xl mx-auto">
              涵盖五大热门品类，严选日本原装正品，满足您的品质生活需求
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="group relative bg-background-50 rounded-lg overflow-hidden border border-background-200/70 transition-all duration-300 hover:border-primary-300"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4 text-center">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{cat.productCount}+ 件商品</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Hot Products Section */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                热卖爆款
              </h2>
              <p className="text-sm text-foreground-500 mt-2">口碑之选，品质保证</p>
            </div>
            <Link
              to="/category/beauty"
              className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
            {hotProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          <div className="mt-8 text-center sm:hidden">
            <Link
              to="/category/beauty"
              className="inline-flex items-center gap-1 px-6 py-2.5 border border-primary-300 text-primary-600 rounded-md text-sm font-medium hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              查看全部商品 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* New Arrivals Section */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              新品上架
            </h2>
            <p className="text-sm text-foreground-500 mt-2">发现最新日本好物，抢先体验</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
            {newProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                精选文章
              </h2>
              <p className="text-sm text-foreground-500 mt-2">购物指南、使用心得、品牌故事</p>
            </div>
            <Link
              to="/articles"
              className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {latestArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
          <div className="mt-8 text-center sm:hidden">
            <Link
              to="/articles"
              className="inline-flex items-center gap-1 px-6 py-2.5 border border-primary-300 text-primary-600 rounded-md text-sm font-medium hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              查看全部文章 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              用户好评
            </h2>
            <p className="text-sm text-foreground-500 mt-2">来自真实用户的信赖与推荐</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.slice(0, 6).map((item) => (
              <div key={item.id} className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: item.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">{item.content}</p>
                <div className="flex items-center justify-between text-xs text-foreground-500">
                  <span className="font-medium text-foreground-800">{item.name}</span>
                  <span>{item.location} · {item.date}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Intro CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20abstract%20Japanese%20texture%20with%20washi%20paper%20pattern%20and%20gentle%20warm%20cream%20tone%2C%20minimal%20artistic%20background%2C%20subtle%20organic%20flow%2C%20no%20text%2C%20clean%20sophisticated%20aesthetic&width=1800&height=600&seq=cta-bg-01&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-background-900/60"></div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            品质严选，值得信赖
          </h2>
          <p className="text-white/80 text-sm md:text-base max-w-2xl mx-auto mb-8 leading-relaxed">
            我们走访日本47个都道府县，与超过200个品牌深度合作，只为给您带来最纯正的日本品质体验
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-white">200+</p>
              <p className="text-white/70 text-sm mt-1">合作品牌</p>
            </div>
            <div className="hidden sm:block w-px h-12 bg-white/20"></div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-white">50,000+</p>
              <p className="text-white/70 text-sm mt-1">满意客户</p>
            </div>
            <div className="hidden sm:block w-px h-12 bg-white/20"></div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-white">100%</p>
              <p className="text-white/70 text-sm mt-1">正品保障</p>
            </div>
          </div>
          <Link
            to="/about"
            className="inline-block mt-8 px-8 py-3 bg-background-50 hover:bg-white text-foreground-900 font-medium rounded-md text-sm transition-colors whitespace-nowrap"
          >
            了解更多
          </Link>
        </div>
      </section>
    </>
  );
}